import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import ThemeToggle from '../components/ThemeToggle';
import {
  Building2,
  Users,
  FileText,
  Calendar,
  TrendingUp,
  Palette,
  Shield,
  MessageSquare,
  ChevronRight,
  Check,
  ArrowRight,
  Menu,
  X,
  Box,
  BarChart3,
  Briefcase,
  Clock,
  Lock,
  Unlock,
  Sparkles
} from 'lucide-react';

// Coming Soon Overlay Component
const ComingSoonOverlay = ({ onUnlock }) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  
  const SECRET_CODE = '8487'; // The 4-digit unlock code
  
  const handleInputChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(false);
    
    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    
    // Check code when all 4 digits entered
    if (index === 3 && value) {
      const fullCode = newCode.join('');
      if (fullCode === SECRET_CODE) {
        setUnlocking(true);
        setTimeout(() => {
          localStorage.setItem('fusion_unlocked', 'true');
          onUnlock();
        }, 1500);
      } else {
        setError(true);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        // Clear after wrong code
        setTimeout(() => {
          setCode(['', '', '', '']);
          inputRefs[0].current?.focus();
        }, 800);
      }
    }
  };
  
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (/^\d{1,4}$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
      setCode(newCode);
      if (pastedData.length === 4) {
        if (pastedData === SECRET_CODE) {
          setUnlocking(true);
          setTimeout(() => {
            localStorage.setItem('fusion_unlocked', 'true');
            onUnlock();
          }, 1500);
        } else {
          setError(true);
          setShake(true);
          setTimeout(() => setShake(false), 500);
          setTimeout(() => {
            setCode(['', '', '', '']);
            inputRefs[0].current?.focus();
          }, 800);
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-20" 
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite'
          }}
        />
        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/30">
            <Building2 className="w-10 h-10 text-white" />
          </div>
        </div>
        
        {/* Title */}
        <h1 className="text-5xl md:text-7xl font-bold mb-4">
          <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
            Coming Soon
          </span>
        </h1>
        
        {/* Subtitle */}
        <p className="text-xl md:text-2xl text-blue-200/80 mb-2 font-light">
          Fusion<span className="text-orange-400 font-semibold">Builder</span>Pro
        </p>
        <p className="text-gray-400 mb-12 max-w-md mx-auto">
          Something extraordinary is being built. Enter the access code to preview.
        </p>
        
        {/* Code Input */}
        <div className={`flex justify-center gap-3 md:gap-4 mb-6 ${shake ? 'animate-shake' : ''}`}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-16 h-20 md:w-20 md:h-24 text-3xl md:text-4xl font-bold text-center rounded-2xl 
                         bg-white/5 backdrop-blur-sm border-2 transition-all duration-300 outline-none
                         ${error 
                           ? 'border-red-500 text-red-400 bg-red-500/10' 
                           : digit 
                             ? 'border-blue-500 text-white bg-blue-500/10' 
                             : 'border-white/20 text-white hover:border-white/40 focus:border-blue-500'
                         }
                         ${unlocking ? 'border-green-500 text-green-400 bg-green-500/10' : ''}`}
              disabled={unlocking}
            />
          ))}
        </div>
        
        {/* Status indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {unlocking ? (
            <>
              <Unlock className="w-5 h-5 text-green-400 animate-pulse" />
              <span className="text-green-400 font-medium">Access Granted</span>
              <Sparkles className="w-5 h-5 text-green-400 animate-pulse" />
            </>
          ) : error ? (
            <>
              <Lock className="w-5 h-5 text-red-400" />
              <span className="text-red-400">Invalid code, try again</span>
            </>
          ) : (
            <>
              <Lock className="w-5 h-5 text-gray-500" />
              <span className="text-gray-500">Enter 4-digit access code</span>
            </>
          )}
        </div>
        
        {/* Footer text */}
        <p className="text-gray-600 text-sm">
          Authorized personnel only
        </p>
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(() => {
    return localStorage.getItem('fusion_unlocked') === 'true';
  });

  const features = [
    {
      icon: Box,
      title: '3D Building Configurator',
      description: 'Interactive real-time 3D visualization for custom metal buildings, carports, garages, and more.'
    },
    {
      icon: Palette,
      title: 'White-Label Branding',
      description: 'Each sales company gets their own branded portal with custom colors, logos, and subdomain.'
    },
    {
      icon: Users,
      title: 'Multi-Company Management',
      description: 'Manage multiple sales companies under one builder organization with hierarchical access.'
    },
    {
      icon: FileText,
      title: 'Quote Generation & Tracking',
      description: 'Create professional PDF quotes, track them through the pipeline, and convert to projects.'
    },
    {
      icon: TrendingUp,
      title: 'Full CRM Suite',
      description: 'Complete lead management, opportunity tracking, and customer relationship tools.'
    },
    {
      icon: Calendar,
      title: 'Scheduling & Booking',
      description: 'Built-in calendar for appointments, installations, and follow-ups.'
    },
    {
      icon: MessageSquare,
      title: 'Project Communication',
      description: 'Collaborate with builders and customers directly within each project.'
    },
    {
      icon: BarChart3,
      title: 'Reports & Analytics',
      description: 'Comprehensive dashboards showing sales performance, pipeline health, and revenue trends.'
    }
  ];

  const builderFeatures = [
    'Manage multiple sales companies',
    'Access projects assigned to you',
    'Real-time pipeline visibility',
    'Custom branding options',
    'Builder-specific analytics'
  ];

  const salesCompanyFeatures = [
    'Your own branded portal',
    'Full CRM functionality',
    'Lead & customer management',
    'Quote generation & tracking',
    'Scheduling & appointments',
    'Assign builders to projects'
  ];

  return (
    <>
      {/* Coming Soon Overlay */}
      {!isUnlocked && <ComingSoonOverlay onUnlock={() => setIsUnlocked(true)} />}
      
      <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-300">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Fusion<span className="text-orange-500">Builder</span>Pro
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Features
              </a>
              <a href="#for-builders" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                For Builders
              </a>
              <a href="#for-sales" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                For Sales Companies
              </a>
              <ThemeToggle />
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                className="border-gray-300 dark:border-gray-600"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <ThemeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 dark:text-gray-300"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 px-4 py-4 space-y-4">
            <a href="#features" className="block text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              Features
            </a>
            <a href="#for-builders" className="block text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              For Builders
            </a>
            <a href="#for-sales" className="block text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              For Sales Companies
            </a>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button className="flex-1 bg-blue-600" onClick={() => navigate('/login')}>
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Multi-Tenant SaaS Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight mb-6">
              The Complete CRM for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-500">
                Metal Building Sales
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Empower your builders and sales companies with a powerful 3D configurator, 
              full CRM suite, and white-label branding — all in one platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-lg px-8 py-6"
                data-testid="hero-get-started-btn"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full sm:w-auto text-lg px-8 py-6 border-gray-300 dark:border-gray-600"
                data-testid="hero-demo-btn"
              >
                <Box className="w-5 h-5 mr-2" />
                Try 3D Demo
              </Button>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-950 to-transparent z-10 pointer-events-none" style={{ height: '30%', bottom: 0, top: 'auto' }} />
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-4 shadow-2xl">
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-800">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="ml-4 text-sm text-gray-400">fusionbuilderpro.com/configurator</span>
                </div>
                <div className="aspect-video relative overflow-hidden">
                  <iframe 
                    src="/demo?embed=true" 
                    className="w-full h-full border-0"
                    title="3D Building Configurator Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/80 to-transparent p-4 pointer-events-none">
                    <p className="text-white text-sm font-medium">Interactive 3D Building Configurator</p>
                    <p className="text-gray-300 text-xs mt-1">Real-time preview • Instant quotes • PDF export</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Scale
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              From 3D visualization to complete CRM — manage your entire metal building sales operation in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow border border-gray-100 dark:border-gray-700"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Builders Section */}
      <section id="for-builders" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 rounded-full text-orange-600 dark:text-orange-400 text-sm font-medium mb-4">
                <Building2 className="w-4 h-4" />
                For Builders
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Grow Your Network of Sales Companies
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                As a metal building manufacturer, you can onboard multiple sales companies, 
                each with their own branded portal. Get visibility into their pipeline and 
                access projects where your services are needed.
              </p>
              <ul className="space-y-4">
                {builderFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-white">
              <Building2 className="w-16 h-16 mb-6 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">Builder Dashboard</h3>
              <p className="text-orange-100 mb-6">
                See all your sales companies, track their performance, and jump into projects where you're assigned.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-3xl font-bold">12</p>
                  <p className="text-orange-200 text-sm">Sales Companies</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-3xl font-bold">48</p>
                  <p className="text-orange-200 text-sm">Active Projects</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Sales Companies Section */}
      <section id="for-sales" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <Briefcase className="w-16 h-16 mb-6 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">Your Branded Portal</h3>
              <p className="text-blue-100 mb-6">
                Get your own subdomain, logo, and color scheme. Your customers see your brand, not ours.
              </p>
              <div className="bg-white/10 rounded-lg p-4 mb-4">
                <p className="text-blue-200 text-sm mb-1">Your Portal URL</p>
                <p className="font-mono text-lg">yourcompany.fusionbuilderpro.com</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">156</p>
                  <p className="text-blue-200 text-xs">Leads</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">$2.4M</p>
                  <p className="text-blue-200 text-xs">Pipeline</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">89%</p>
                  <p className="text-blue-200 text-xs">Close Rate</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 text-sm font-medium mb-4">
                <Users className="w-4 h-4" />
                For Sales Companies
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Your Complete Sales Platform
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                Run your entire sales operation from one platform. Manage leads, create quotes with 
                the 3D configurator, track opportunities, and collaborate with builders — all under your own brand.
              </p>
              <ul className="space-y-4">
                {salesCompanyFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Transform Your Sales Process?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            Join the growing network of builders and sales companies using Fusion Builder Pro.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-lg px-8 py-6"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="w-full sm:w-auto text-lg px-8 py-6 border-gray-300 dark:border-gray-600"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">
                  Fusion<span className="text-orange-500">Builder</span>Pro
                </span>
              </div>
              <p className="text-gray-400 max-w-md">
                The complete multi-tenant SaaS platform for metal building manufacturers and their sales networks.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#for-builders" className="hover:text-white transition-colors">For Builders</a></li>
                <li><a href="#for-sales" className="hover:text-white transition-colors">For Sales Companies</a></li>
                <li><Link to="/demo" className="hover:text-white transition-colors">3D Configurator Demo</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Fusion Builder Pro. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/admin-login" 
                className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                data-testid="footer-admin-login-link"
              >
                Platform Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
